import { useState, useRef, useEffect } from 'react';
import { format, addDays, setHours, setMinutes } from 'date-fns';
import { Button } from '@/components/ui/button';
import { PendingSessionInfo } from '@/api/chat';
import { useAcceptProposal, useSessionProposeTime, useCounterPropose, type ProposeTimePayload } from '@/hooks/useSessions';
import { Check, ChevronDown, ChevronUp, Clock, Edit2, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

interface SessionQuickActionPanelProps {
  pendingSessions: PendingSessionInfo[];
  onActionComplete?: () => void;
}

// Generate next 14 days of dates
function generateDates(): Date[] {
  const dates: Date[] = [];
  const today = new Date();
  for (let i = 0; i < 14; i++) {
    dates.push(addDays(today, i));
  }
  return dates;
}

// Generate hours (7 AM to 10 PM)
function generateHours(): number[] {
  return Array.from({ length: 16 }, (_, i) => i + 7);
}

// Generate minutes (00, 15, 30, 45)
function generateMinutes(): number[] {
  return [0, 15, 30, 45];
}

// Wheel picker component for mobile-style selection
interface WheelPickerProps<T> {
  items: T[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  renderItem: (item: T, isSelected: boolean) => React.ReactNode;
  height?: number;
}

function WheelPicker<T>({ items, selectedIndex, onSelect, renderItem, height = 120 }: WheelPickerProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemHeight = 40;
  
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = selectedIndex * itemHeight;
    }
  }, [selectedIndex]);

  const handleScroll = () => {
    if (containerRef.current) {
      const scrollTop = containerRef.current.scrollTop;
      const newIndex = Math.round(scrollTop / itemHeight);
      if (newIndex !== selectedIndex && newIndex >= 0 && newIndex < items.length) {
        onSelect(newIndex);
      }
    }
  };

  return (
    <div className="relative" style={{ height }}>
      {/* Selection indicator - minimal border only */}
      <div 
        className="absolute left-1 right-1 border-y-2 border-amber-400 pointer-events-none z-10 rounded"
        style={{ 
          top: (height - itemHeight) / 2, 
          height: itemHeight 
        }}
      />
      
      {/* Scroll container */}
      <div
        ref={containerRef}
        className="h-full overflow-y-auto scrollbar-hide scroll-smooth snap-y snap-mandatory"
        onScroll={handleScroll}
        style={{ 
          scrollSnapType: 'y mandatory',
          paddingTop: (height - itemHeight) / 2,
          paddingBottom: (height - itemHeight) / 2,
        }}
      >
        {items.map((item, index) => (
          <div
            key={index}
            className="snap-center flex items-center justify-center cursor-pointer transition-all"
            style={{ height: itemHeight }}
            onClick={() => {
              onSelect(index);
              if (containerRef.current) {
                containerRef.current.scrollTo({
                  top: index * itemHeight,
                  behavior: 'smooth'
                });
              }
            }}
          >
            {renderItem(item, index === selectedIndex)}
          </div>
        ))}
      </div>
      
      {/* Subtle gradient overlays for fade effect */}
      <div className="absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-white/80 to-transparent pointer-events-none z-20" />
      <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white/80 to-transparent pointer-events-none z-20" />
    </div>
  );
}

// Single pending session action card
interface SessionActionCardProps {
  session: PendingSessionInfo;
  onActionComplete?: () => void;
}

function SessionActionCard({ session, onActionComplete }: SessionActionCardProps) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  
  // Date/time picker state
  const dates = generateDates();
  const hours = generateHours();
  const minutes = generateMinutes();
  
  // Parse proposed time to get initial indices
  const getProposedIndices = () => {
    if (session.proposedTime) {
      const d = new Date(session.proposedTime);
      const dateIdx = dates.findIndex(date => date.toDateString() === d.toDateString());
      const hourIdx = hours.indexOf(d.getHours());
      const minuteIdx = minutes.indexOf(Math.floor(d.getMinutes() / 15) * 15);
      return {
        dateIndex: dateIdx >= 0 ? dateIdx : 1,
        hourIndex: hourIdx >= 0 ? hourIdx : 3,
        minuteIndex: minuteIdx >= 0 ? minuteIdx : 0
      };
    }
    return { dateIndex: 1, hourIndex: 3, minuteIndex: 0 }; // Tomorrow at 10:00 AM
  };
  
  const proposed = getProposedIndices();
  const [selectedDateIndex, setSelectedDateIndex] = useState(proposed.dateIndex);
  const [selectedHourIndex, setSelectedHourIndex] = useState(proposed.hourIndex);
  const [selectedMinuteIndex, setSelectedMinuteIndex] = useState(proposed.minuteIndex);
  const [duration, setDuration] = useState(session.duration || 60);
  
  // Reset to proposed time when entering edit mode
  useEffect(() => {
    if (isEditing) {
      const indices = getProposedIndices();
      setSelectedDateIndex(indices.dateIndex);
      setSelectedHourIndex(indices.hourIndex);
      setSelectedMinuteIndex(indices.minuteIndex);
    }
  }, [isEditing]);
  
  const acceptMutation = useAcceptProposal(session.proposalId || null);
  const proposeTimeMutation = useSessionProposeTime(session.sessionId);
  const counterProposeMutation = useCounterPropose(session.proposalId || null);
  
  const handleAccept = () => {
    if (session.proposalId) {
      acceptMutation.mutate(undefined, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['chat', 'channels'] });
          onActionComplete?.();
        }
      });
    }
  };
  
  const handlePropose = () => {
    const selectedDate = dates[selectedDateIndex];
    const selectedHour = hours[selectedHourIndex];
    const selectedMinute = minutes[selectedMinuteIndex];
    
    let proposedDateTime = setHours(selectedDate, selectedHour);
    proposedDateTime = setMinutes(proposedDateTime, selectedMinute);
    
    const payload: ProposeTimePayload = {
      proposedStartTime: proposedDateTime.toISOString(),
      duration,
      holdExpiryHours: 24
    };
    
    // Use counter-propose if there's an existing proposal, otherwise propose new
    if (session.proposalId && !session.waitingForCurrentUser) {
      counterProposeMutation.mutate(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['chat', 'channels'] });
          setIsEditing(false);
          onActionComplete?.();
        }
      });
    } else {
      proposeTimeMutation.mutate(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['chat', 'channels'] });
          setIsEditing(false);
          onActionComplete?.();
        }
      });
    }
  };
  
  const isLoading = acceptMutation.isPending || proposeTimeMutation.isPending || counterProposeMutation.isPending;
  
  // Determine what actions to show
  const canAccept = session.waitingForCurrentUser && session.proposalId;
  const showProposedTime = session.proposedTime && !isEditing;
  
  if (!expanded) {
    // Collapsed view - stacked for narrow panel
    return (
      <div 
        className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 cursor-pointer hover:bg-amber-100 transition-colors"
        onClick={() => setExpanded(true)}
      >
        {/* Top row: Subject + expand icon */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-600 flex-shrink-0" />
            <span className="font-medium text-amber-800 text-sm truncate">{session.subject}</span>
          </div>
          <ChevronDown className="h-4 w-4 text-amber-600 flex-shrink-0" />
        </div>
        {/* Bottom row: Time + Status */}
        <div className="flex items-center justify-between pl-6">
          {session.proposedTime && (
            <span className="text-amber-700 text-xs font-medium">
              {format(new Date(session.proposedTime), 'EEE, MMM d • h:mm a')}
            </span>
          )}
          <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full ml-auto">
            {session.pendingDescription}
          </span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-amber-100 transition-colors border-b border-amber-200"
        onClick={() => setExpanded(false)}
      >
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-600" />
          <span className="font-medium text-amber-800">{session.subject}</span>
          <span className="text-xs text-amber-600">with {session.otherPartyName}</span>
        </div>
        <ChevronUp className="h-4 w-4 text-amber-600" />
      </div>
      
      {/* Content */}
      <div className="p-3">
        {showProposedTime && session.proposedTime && (
          <div className="mb-3 p-3 bg-white rounded-lg border border-amber-100">
            <div className="text-xs text-amber-600 mb-1">
              {session.proposedBy === 'STUDENT' ? 'Student proposed' : 'Tutor proposed'}
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-gray-900">
                  {format(new Date(session.proposedTime), 'EEEE, MMMM d')}
                </div>
                <div className="text-lg font-bold text-amber-700">
                  {format(new Date(session.proposedTime), 'h:mm a')}
                </div>
                {session.duration && (
                  <div className="text-sm text-gray-500">{session.duration} minutes</div>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
                className="text-amber-600 hover:text-amber-700 hover:bg-amber-100"
              >
                <Edit2 className="h-4 w-4 mr-1" />
                Change
              </Button>
            </div>
          </div>
        )}
        
        {isEditing && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Pick a new time</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(false)}
                className="text-gray-500 hover:text-gray-700 h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Wheel pickers */}
            <div className="flex gap-2 bg-white rounded-lg border border-amber-100 p-2">
              {/* Date picker */}
              <div className="flex-1">
                <div className="text-xs text-center text-gray-500 mb-1">Date</div>
                <WheelPicker
                  items={dates}
                  selectedIndex={selectedDateIndex}
                  onSelect={setSelectedDateIndex}
                  renderItem={(date, isSelected) => (
                    <span className={`text-sm ${isSelected ? 'font-bold text-amber-700' : 'text-gray-600'}`}>
                      {format(date, 'EEE, MMM d')}
                    </span>
                  )}
                />
              </div>
              
              {/* Hour picker */}
              <div className="w-16">
                <div className="text-xs text-center text-gray-500 mb-1">Hour</div>
                <WheelPicker
                  items={hours}
                  selectedIndex={selectedHourIndex}
                  onSelect={setSelectedHourIndex}
                  renderItem={(hour, isSelected) => (
                    <span className={`text-sm ${isSelected ? 'font-bold text-amber-700' : 'text-gray-600'}`}>
                      {hour > 12 ? hour - 12 : hour} {hour >= 12 ? 'PM' : 'AM'}
                    </span>
                  )}
                />
              </div>
              
              {/* Minute picker */}
              <div className="w-14">
                <div className="text-xs text-center text-gray-500 mb-1">Min</div>
                <WheelPicker
                  items={minutes}
                  selectedIndex={selectedMinuteIndex}
                  onSelect={setSelectedMinuteIndex}
                  renderItem={(minute, isSelected) => (
                    <span className={`text-sm ${isSelected ? 'font-bold text-amber-700' : 'text-gray-600'}`}>
                      :{minute.toString().padStart(2, '0')}
                    </span>
                  )}
                />
              </div>
            </div>
            
            {/* Duration selector */}
            <div className="flex items-center gap-2 mt-2">
              <span className="text-sm text-gray-600">Duration:</span>
              <select
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="text-sm border border-amber-200 rounded px-2 py-1 bg-white"
              >
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
                <option value={120}>2 hours</option>
              </select>
            </div>
          </div>
        )}
        
        {/* Action buttons */}
        <div className="flex gap-2">
          {canAccept && !isEditing && (
            <Button
              size="sm"
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={handleAccept}
              disabled={isLoading}
            >
              <Check className="h-4 w-4 mr-1" />
              Accept
            </Button>
          )}
          
          {isEditing && (
            <Button
              size="sm"
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              onClick={handlePropose}
              disabled={isLoading}
            >
              {session.proposalId ? 'Edit Session' : 'Propose Time'}
            </Button>
          )}
          
          {!isEditing && !canAccept && (
            <div className="flex-1 text-center text-sm text-amber-600 py-2">
              {session.pendingDescription}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function SessionQuickActionPanel({ pendingSessions, onActionComplete }: SessionQuickActionPanelProps) {
  if (pendingSessions.length === 0) {
    return null;
  }
  
  return (
    <div className="p-3 border-b border-stone-200 bg-stone-50 space-y-2">
      <div className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-2">
        Pending Sessions ({pendingSessions.length})
      </div>
      {pendingSessions.map((session) => (
        <SessionActionCard
          key={session.sessionId}
          session={session}
          onActionComplete={onActionComplete}
        />
      ))}
    </div>
  );
}
